import { ClipboardEvent, KeyboardEvent, startTransition, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import {
  ARROW_DOWN_KEY,
  ARROW_LEFT_KEY,
  ARROW_RIGHT_KEY,
  ARROW_UP_KEY,
  BACKSPACE_KEY,
  DELETE_KEY,
  ENTER_KEY,
  ESCAPE_KEY,
  F2_KEY,
  KEY_C,
  KEY_V,
} from 'common/constants/general';
import { getInlineNoteDisplayText } from './InlineNoteEditor.utilities';

interface InlineNoteEditorProps {
  value?: string | null;
  onSave: (note: string) => Promise<void> | void;
  isLoading?: boolean;
  onActiveStateChange?: (isActive: boolean) => void;
}

const normalizeNoteValue = (value?: string | null): string => value || '';
const NOTE_SELECTION_EVENT = 'inline-note-editor-selection-change';
const NOTE_MULTI_PASTE_EVENT = 'inline-note-editor-multi-paste';
const selectedEditorIds = new Set<string>();
// Anchor cell for Excel-like range selection (set when Shift+Arrow starts)
let anchorEditorId: string | null = null;

const emitSelectionChange = () => {
  globalThis.dispatchEvent(
    new CustomEvent<string[]>(NOTE_SELECTION_EVENT, {
      detail: Array.from(selectedEditorIds),
    })
  );
};

// Updates multi-select state for vertical arrow navigation between note cells.
// Implements Excel-like range selection: Shift+Down extends, Shift+Up shrinks.
// Focus movement is handled by the parent table's useKeyboardNavigation hook.
const updateArrowSelection = (event: KeyboardEvent<HTMLDivElement>, shiftKey: boolean) => {
  const tbody = event.currentTarget.closest('tbody');
  const editors = tbody
    ? Array.from(tbody.querySelectorAll<HTMLElement>('[data-inline-note-editor="true"][tabindex="0"]'))
    : [];
  const currentIdx = editors.indexOf(event.currentTarget);
  const nextIdx = event.key === ARROW_DOWN_KEY ? currentIdx + 1 : currentIdx - 1;
  const next = editors[nextIdx];

  if (!shiftKey) {
    selectedEditorIds.clear();
    anchorEditorId = null;
    emitSelectionChange();
    return;
  }

  if (!next) {
    return; // No cell to navigate to
  }

  // Establish anchor on the first Shift+Arrow press
  if (!anchorEditorId) {
    anchorEditorId = event.currentTarget.dataset.editorId ?? null;
  }

  // Resolve anchor index; fall back to current if anchor element left the DOM
  const anchorEl = anchorEditorId ? editors.find(el => el.dataset.editorId === anchorEditorId) : null;
  const anchorIdx = anchorEl ? editors.indexOf(anchorEl) : currentIdx;

  // Recompute selection as the contiguous range [anchor … nextIdx]
  const minIdx = Math.min(anchorIdx, nextIdx);
  const maxIdx = Math.max(anchorIdx, nextIdx);
  selectedEditorIds.clear();
  editors.slice(minIdx, maxIdx + 1).forEach(el => {
    const id = el.dataset.editorId;
    if (id) {
      selectedEditorIds.add(id);
    }
  });
  emitSelectionChange();
};

const InlineNoteEditor = ({
  value,
  onSave,
  isLoading = false,
  onActiveStateChange,
}: InlineNoteEditorProps) => {
  const editorIdRef = useRef(useId());
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const displayBoxRef = useRef<HTMLDivElement | null>(null);
  const skipBlurSaveRef = useRef(false);
  const isSavingRef = useRef(false);
  // Tracks whether focus should be restored to the display box after editing ends
  const refocusAfterEditRef = useRef(false);
  const wasEditingRef = useRef(false);
  const [isSelected, setIsSelected] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(getInlineNoteDisplayText(value));
  const [placeCursorAtEnd, setPlaceCursorAtEnd] = useState(false);

  const normalizedValue = useMemo(() => normalizeNoteValue(value), [value]);
  const displayValue = useMemo(() => getInlineNoteDisplayText(value), [value]);

  useEffect(() => {
    if (!isEditing) {
      setDraftValue(displayValue);
    }
  }, [displayValue, isEditing]);

  useEffect(() => {
    if (onActiveStateChange) {
      onActiveStateChange(isSelected || isEditing);
    }
  }, [isSelected, isEditing, onActiveStateChange]);

  useEffect(() => {
    const handleExternalSelection = (event: Event) => {
      const customEvent = event as CustomEvent<string[]>;
      const selectedIds = customEvent.detail || [];
      startTransition(() => {
        setIsSelected(selectedIds.includes(editorIdRef.current));
      });
    };

    globalThis.addEventListener(NOTE_SELECTION_EVENT, handleExternalSelection);
    return () => {
      globalThis.removeEventListener(NOTE_SELECTION_EVENT, handleExternalSelection);
    };
  }, []);

  useEffect(() => {
    const handleOutsidePointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-inline-note-editor="true"]')) {
        return;
      }

      if (selectedEditorIds.size > 0) {
        selectedEditorIds.clear();
        emitSelectionChange();
      }
    };

    document.addEventListener('mousedown', handleOutsidePointerDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsidePointerDown);
    };
  }, []);

  useEffect(() => {
    const handleMultiPaste = async (event: Event) => {
      const customEvent = event as CustomEvent<{ sourceId: string; text: string; targetIds: string[] }>;
      const payload = customEvent.detail;
      if (!payload || payload.sourceId === editorIdRef.current || isEditing || isLoading) {
        return;
      }
      if (payload.targetIds.includes(editorIdRef.current)) {
        await onSave(payload.text);
      }
    };

    globalThis.addEventListener(NOTE_MULTI_PASTE_EVENT, handleMultiPaste);
    return () => {
      globalThis.removeEventListener(NOTE_MULTI_PASTE_EVENT, handleMultiPaste);
    };
  }, [isEditing, isLoading, onSave]);

  useEffect(() => {
    const editorId = editorIdRef.current;
    return () => {
      if (selectedEditorIds.delete(editorId)) {
        // Defer to avoid dispatching during React's commit/cleanup phase,
        // which would trigger setState calls in other mounted editors mid-render.
        queueMicrotask(emitSelectionChange);
      }
      if (anchorEditorId === editorId) {
        anchorEditorId = null;
      }
    };
  }, []);

  const replaceSelection = () => {
    selectedEditorIds.clear();
    selectedEditorIds.add(editorIdRef.current);
    anchorEditorId = editorIdRef.current;
    emitSelectionChange();
  };

  const toggleSelection = () => {
    if (selectedEditorIds.has(editorIdRef.current)) {
      selectedEditorIds.delete(editorIdRef.current);
    } else {
      selectedEditorIds.add(editorIdRef.current);
    }
    emitSelectionChange();
  };

  // Restore focus to the display box after editing ends (Escape or Enter), but not on blur-to-save
  useEffect(() => {
    if (wasEditingRef.current && !isEditing && refocusAfterEditRef.current) {
      refocusAfterEditRef.current = false;
      displayBoxRef.current?.focus();
    }
    wasEditingRef.current = isEditing;
  }, [isEditing]);

  useEffect(() => {
    if (isEditing && placeCursorAtEnd && textAreaRef.current) {
      const end = textAreaRef.current.value.length;
      textAreaRef.current.selectionStart = end;
      textAreaRef.current.selectionEnd = end;
      setPlaceCursorAtEnd(false);
    }
  }, [isEditing, placeCursorAtEnd]);

  const startEditing = (initialValue: string, shouldPlaceCursorAtEnd: boolean) => {
    if (isLoading) {
      return;
    }
    replaceSelection();
    setDraftValue(initialValue);
    setPlaceCursorAtEnd(shouldPlaceCursorAtEnd);
    setIsSelected(true);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    refocusAfterEditRef.current = true;
    setDraftValue(displayValue);
    setIsEditing(false);
  };

  const saveAndClose = async (keepSelected: boolean) => {
    if (isLoading || isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    try {
      if (draftValue !== displayValue) {
        await onSave(draftValue);
      }
      // keepSelected = true means Enter was pressed; restore focus so arrow nav continues
      refocusAfterEditRef.current = keepSelected;
      setIsEditing(false);
      setIsSelected(keepSelected);
    } finally {
      isSavingRef.current = false;
    }
  };

  // Dispatch paste to all currently selected editors, then save to this one.
  // Works whether this cell is the only focused cell or part of a multi-selection.
  const applyPaste = async (pastedText: string) => {
    if (isEditing || isLoading || !pastedText) {
      return;
    }

    const targetIds = Array.from(selectedEditorIds);
    if (!targetIds.includes(editorIdRef.current)) {
      targetIds.push(editorIdRef.current);
    }

    globalThis.dispatchEvent(
      new CustomEvent(NOTE_MULTI_PASTE_EVENT, {
        detail: { sourceId: editorIdRef.current, text: pastedText, targetIds },
      })
    );

    await onSave(pastedText);
  };

  const handleEditingKeyDown = async (event: KeyboardEvent<HTMLDivElement>) => {
    // While editing, prevent all key events from bubbling up to the table's
    // keyboard navigation handler — arrow keys and Ctrl+Arrow should move the
    // cursor inside the textarea, not navigate to another cell.
    event.stopPropagation();

    if (event.key === ENTER_KEY && !event.shiftKey) {
      event.preventDefault();
      skipBlurSaveRef.current = true;
      await saveAndClose(true);
      return;
    }

    if (event.key === ESCAPE_KEY) {
      event.preventDefault();
      cancelEditing();
    }
  };

  const handleClipboardOrInputKey = async (event: KeyboardEvent<HTMLDivElement>): Promise<boolean> => {
    const isMod = event.metaKey || event.ctrlKey;
    if (isMod && event.key.toLowerCase() === KEY_C) {
      event.preventDefault();
      await navigator.clipboard.writeText(normalizedValue);
      return true;
    }
    if (isMod && event.key.toLowerCase() === KEY_V) {
      event.preventDefault();
      await applyPaste(await navigator.clipboard.readText());
      return true;
    }
    if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      event.preventDefault();
      startEditing(event.key, true);
      return true;
    }
    if (event.key === BACKSPACE_KEY || event.key === DELETE_KEY) {
      event.preventDefault();
      startEditing('', true);
      return true;
    }
    return false;
  };

  const handleSelectedKeyDown = async (event: KeyboardEvent<HTMLDivElement>) => {
    // Vertical arrows: update multi-select state, then let useKeyboardNavigation move focus.
    if (event.key === ARROW_DOWN_KEY || event.key === ARROW_UP_KEY) {
      event.preventDefault();
      updateArrowSelection(event, event.shiftKey);
      return; // no stopPropagation — bubbles up to the table's keyboard navigation handler
    }
    // Horizontal arrows: clear selection and let useKeyboardNavigation handle freely.
    if (event.key === ARROW_LEFT_KEY || event.key === ARROW_RIGHT_KEY) {
      if (selectedEditorIds.size > 0) {
        selectedEditorIds.clear();
        emitSelectionChange();
      }
      return; // no stopPropagation — bubbles up to the table's keyboard navigation handler
    }
    event.stopPropagation();
    if (event.key === F2_KEY || event.key === ENTER_KEY) {
      event.preventDefault();
      startEditing(displayValue, true);
      return;
    }
    await handleClipboardOrInputKey(event);
  };

  const handlePaste = async (event: ClipboardEvent<HTMLDivElement>) => {
    if (isEditing || isLoading) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    await applyPaste(event.clipboardData.getData('text'));
  };

  const hasValue = displayValue.trim() !== '';
  const displayText = hasValue ? displayValue : '-';
  const collapsedDisplayText = displayText.replaceAll('\n', ' ');
  const shouldShowTitle = displayText.includes('\n');
  const rowsCount = draftValue.includes('\n') ? 2 : 1;

  if (isEditing) {
    return (
      <Box
        data-inline-note-editor="true"
        sx={{
          width: '100%',
          height: '100%',
          minHeight: '100%',
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          boxSizing: 'border-box',
        }}>
        <TextField
          autoFocus
          multiline
          fullWidth
          minRows={rowsCount}
          maxRows={10}
          value={draftValue}
          onChange={event => setDraftValue(event.target.value)}
          onKeyDown={handleEditingKeyDown}
          placeholder="Add note"
          variant="standard"
          size="small"
          disabled={isLoading}
          onClick={event => event.stopPropagation()}
          onBlur={async () => {
            if (skipBlurSaveRef.current) {
              skipBlurSaveRef.current = false;
              return;
            }
            await saveAndClose(false);
          }}
          inputRef={textAreaRef}
          slotProps={{
            input: { disableUnderline: true },
          }}
          sx={{
            width: '100%',
            '& .MuiInputBase-root': {
              fontSize: '0.875rem',
              lineHeight: 1.43,
              padding: 0,
            },
            '& .MuiInputBase-inputMultiline': {
              padding: 0,
              overflowY: 'auto',
            },
          }}
        />
      </Box>
    );
  }

  const text = (
    <Typography
      variant="body2"
      sx={{
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        color: hasValue ? 'text.primary' : 'text.secondary',
        fontStyle: hasValue ? 'normal' : 'italic',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
      {collapsedDisplayText}
    </Typography>
  );

  const activeCursor = isSelected || isEditing ? 'text' : 'pointer';
  const cursorStyle = isLoading ? 'default' : activeCursor;

  return (
    <Box
      ref={displayBoxRef}
      data-inline-note-editor="true"
      data-editor-id={editorIdRef.current}
      tabIndex={0}
      onFocus={() => {
        if (selectedEditorIds.size === 0) {
          replaceSelection();
        } else {
          setIsSelected(selectedEditorIds.has(editorIdRef.current));
        }
      }}
      onBlur={() => {
        // Don't deselect if this cell is still part of a multi-selection
        if (!selectedEditorIds.has(editorIdRef.current)) {
          setIsSelected(false);
        }
      }}
      onKeyDown={handleSelectedKeyDown}
      onPaste={event => handlePaste(event)}
      onClick={event => {
        event.stopPropagation();
        event.currentTarget.focus();
        if (event.metaKey || event.ctrlKey || event.shiftKey) {
          toggleSelection();
        } else {
          replaceSelection();
          setIsSelected(true);
        }
      }}
      onDoubleClick={event => {
        event.stopPropagation();
        replaceSelection();
        startEditing(displayValue, true);
      }}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '100%',
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 1,
        boxSizing: 'border-box',
        overflow: 'hidden',
        outline: 'none',
        cursor: cursorStyle,
      }}>
      {shouldShowTitle ? (
        <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%', overflow: 'hidden' }} title={displayText}>
          {text}
        </Box>
      ) : (
        text
      )}
    </Box>
  );
};

export default InlineNoteEditor;
